import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import TooltipWrapper from '@/components/ui/tooltip';
import FavoriteButton from '@/features/markets/components/favorite-button';
import MarketsTable from '@/features/markets/components/markets-table';
import useFundedAccount, {
  getCapitalMaxLeverage,
} from '@/hooks/use-funded-account';
import useSymbol from '@/hooks/use-symbol';
import { useMarketStore } from '@/store/use-markets-store';
import { cn } from '@/utils/cn';
import { abbrFromSym, imgFromSym } from '@/utils/token-symbol';
import { formatNumber } from '@/utils/value-format';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { getMaxLeverage } from '../order/utils/math';

export default function MarketsDropdown() {
  const symbol = useSymbol();
  const [isOpen, setIsOpen] = useState(false);

  const { isolated, initMarginRatio } = useStore(
    useMarketStore(symbol),
    useShallow((state) => ({
      isolated: state.marketSpec.isolated,
      initMarginRatio: state.marketSpec.initMarginRatio,
    })),
  );

  const { isFundedOrEval } = useFundedAccount();

  const maxLeverage: string | undefined = useMemo(() => {
    const maxLeverage = getMaxLeverage(initMarginRatio);
    if (isFundedOrEval && maxLeverage?.bigint !== undefined) {
      const capitalMaxLeverage = getCapitalMaxLeverage(symbol);
      if (maxLeverage.bigint > BigInt(capitalMaxLeverage) * 10n ** 2n) {
        return capitalMaxLeverage.toString();
      }
    }
    return maxLeverage?.decimal;
  }, [symbol, initMarginRatio, isFundedOrEval]);

  const handleRowClick = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className="min-w-max border-r border-border">
          <div className="flex h-full gap-4 bg-background pl-6 transition-colors hover:bg-vestgrey-800 sm:pl-7">
            <div className="hidden self-center md:block">
              <FavoriteButton symbol={symbol} size={18} />
            </div>
            <div className="grid grow">
              <button className="flex items-center justify-between gap-3 pr-6 sm:gap-12 sm:pr-7">
                <div className="flex min-w-max items-center gap-3">
                  <img
                    src={imgFromSym(symbol)}
                    alt="Token Image"
                    className="h-6 w-6 rounded-full object-contain"
                  />
                  <div className="flex flex-col items-start">
                    <span className="text-font text-lg font-bold">
                      {abbrFromSym(symbol)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {maxLeverage && (
                      <div className="hidden items-center justify-center rounded bg-primary px-2 py-1.5 sm:flex">
                        <span className="font-mono leading-none text-primary-foreground">
                          {formatNumber(maxLeverage)}X
                        </span>
                      </div>
                    )}
                    {isolated && (
                      <TooltipWrapper
                        content={
                          <span>
                            Backed by isolated liquidity pool. See{' '}
                            <Link
                              className="underline"
                              target="_blank"
                              rel="noreferrer"
                              to="https://docs.vest.exchange/overview/traders/perpetual-contract-specifications"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Docs
                            </Link>{' '}
                            for details.
                          </span>
                        }
                      >
                        <div className="flex items-center justify-center rounded bg-primary px-1 max-sm:h-5 sm:px-2 sm:py-1.5">
                          <span className="font-mono uppercase leading-none text-primary-foreground max-sm:text-xs sm:mr-px">
                            ISO
                          </span>
                        </div>
                      </TooltipWrapper>
                    )}
                  </div>
                </div>
                <div>
                  <ChevronDownIcon
                    strokeWidth={1.5}
                    className={cn(
                      'text-vestgrey-100 transition-transform duration-300',
                      {
                        'rotate-180': isOpen,
                      },
                    )}
                  />
                </div>
              </button>
            </div>
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-screen max-w-8xl border-0 p-0"
        sideOffset={0}
        align="start"
      >
        <div className="relative w-full rounded-br-md border border-l-0 border-border bg-background">
          <MarketsTable
            id="markets-dropdown"
            variant="dropdown"
            containerClassName="relative h-[336px]"
            tableClassName="sm:[&_td:first-child]:pl-6 sm:[&_td:last-child]:pr-6 [&_td]:h-auto [&_td]:bg-background [&_td]:text-base [&_td]:py-2 sm:[&_th:first-child]:pl-6 sm:[&_th:last-child]:pr-6 [&_th]:h-10 dark:[&_th]:bg-background [&_th]:bg-vestgrey-850 [&_th]:py-0 [&_tr]:cursor-pointer"
            isInfiniteScroll
            infiniteScrollEstimateRowSize={30}
            infiniteScrollOverscan={20}
            disableDelay
            onRowClick={handleRowClick}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
